import { useState, useCallback } from 'react'
import 'maplibre-gl/dist/maplibre-gl.css'
import Map, { Marker, NavigationControl } from 'react-map-gl/maplibre'
import type { ViewStateChangeEvent } from 'react-map-gl/maplibre'
import { useNavigate } from 'react-router-dom'
import type { Device } from '../../types/device.types'
import { ROUTES } from '../../utils/constants'

interface DeviceListMapViewProps {
  devices: Device[]
}

const MAP_STYLE = 'https://tiles.openfreemap.org/styles/liberty'

const DEFAULT_VIEW = {
  longitude: 0,
  latitude: 20,
  zoom: 2,
}

export default function DeviceListMapView({ devices }: DeviceListMapViewProps) {
  const navigate = useNavigate()
  const [viewState, setViewState] = useState(DEFAULT_VIEW)

  const onMove = useCallback((evt: ViewStateChangeEvent) => {
    setViewState(evt.viewState)
  }, [])

  const devicesWithLocation = devices.filter((d) => d.geolocation)

  return (
    <div style={{ position: 'relative', width: '100%', height: 'calc(100vh - 250px)', minHeight: 400, borderRadius: 12, overflow: 'hidden' }}>
      <Map
        {...viewState}
        onMove={onMove}
        mapStyle={MAP_STYLE}
        style={{ width: '100%', height: '100%' }}
        minZoom={2}
        maxZoom={18}
        attributionControl={false}
      >
        <NavigationControl position="bottom-left" />
        {devicesWithLocation.map((device) => (
          <Marker
            key={device.device_id}
            longitude={device.geolocation!.lon}
            latitude={device.geolocation!.lat}
            anchor="center"
          >
            <div
              onClick={() => navigate(ROUTES.deviceDetails(device.device_id))}
              style={{ cursor: 'pointer', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              title={device.device_id}
            >
              {device.isOnline && (
                <div
                  style={{
                    position: 'absolute',
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: '#6366f1',
                    animation: 'marker-pulse 2s cubic-bezier(0, 0, 0.2, 1) infinite',
                  }}
                />
              )}
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background: device.isOnline ? '#6366f1' : '#94a3b8',
                  boxShadow: device.isOnline
                    ? '0 4px 12px rgba(99, 102, 241, 0.4)'
                    : '0 4px 12px rgba(0, 0, 0, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z"
                    fill="#ffffff"
                  />
                </svg>
              </div>
            </div>
          </Marker>
        ))}
      </Map>
      {devicesWithLocation.length === 0 && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(12px)',
              borderRadius: 12,
              padding: '24px 32px',
              textAlign: 'center',
            }}
          >
            <p style={{ fontSize: 14, color: '#475569', margin: 0 }}>
              No devices with location data
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
